import type { BoxProps } from '@mui/material/Box';

import { toast } from 'sonner';
import { keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import InputAdornment from '@mui/material/InputAdornment';

import { useTranslate } from 'src/locales';
import CardDetailsDialog from 'src/pages/setting/components/payment/moyasar-payment';
import { AddBalanceDialog } from 'src/pages/setting/components/payment/AddBalanceDialog';
import {
  useAddBalanceMutation,
  useChargeEstimateMutation,
  useGetLowBalanceMutation,
} from 'src/pages/setting/slice/settingApi';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

interface BalanceData {
  balance: number;
  balance_accountant: number;
  balance_avail: number;
}

interface BalanceApiResponse {
  data?: {
    balance?: BalanceData;
  };
}

export interface BalanceDisplayProps extends BoxProps {
  variant?: 'default' | 'compact';
  isSuperAdmin?: boolean;
  noActivePlan?: boolean;
}

// Animation keyframes
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const pulseRed = keyframes`
  0% { opacity: 1; color: inherit; }
  50% { opacity: 0.5; color: #ff0000; }
  100% { opacity: 1; color: inherit; }
`;

const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  50% { transform: translateX(3px); }
  75% { transform: translateX(-3px); }
  100% { transform: translateX(0); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const checkmarkAnimation = keyframes`
  0% { stroke-dashoffset: 50; }
  100% { stroke-dashoffset: 0; }
`;

const slideDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
    maxHeight: 0;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    maxHeight: 200px;
  }
`;

const slideUp = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
    maxHeight: 200px;
  }
  100% {
    opacity: 0;
    transform: translateY(-10px);
    maxHeight: 0;
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const moneyFloat = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 1;
  }
  100% {
    transform: translateY(-40px) rotate(360deg);
    opacity: 0;
  }
`;

const ripple = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const numberIncrement = keyframes`
  0% {
    transform: scale(1);
    color: inherit;
  }
  50% {
    transform: scale(1.1);
    color: #4caf50;
  }
  100% {
    transform: scale(1);
    color: inherit;
  }
`;

const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
  }
`;

const balanceUpdateGlow = keyframes`
  0% {
    transform: scale(1);
    color: inherit;
    text-shadow: none;
  }
  25% {
    transform: scale(1.05);
    color: #4caf50;
    text-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
  }
  50% {
    transform: scale(1.08);
    color: #4caf50;
    text-shadow: 0 0 15px rgba(76, 175, 80, 0.7);
  }
  75% {
    transform: scale(1.03);
    color: #4caf50;
    text-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
  }
  100% {
    transform: scale(1);
    color: inherit;
    text-shadow: none;
  }
`;

const countUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  50% {
    opacity: 1;
    transform: translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animation components
const PulseAnimation = ({
  children,
  loading,
  lowBalance,
}: {
  children: React.ReactNode;
  loading: boolean;
  lowBalance: boolean;
}) => (
  <Box
    sx={{
      animation: loading
        ? `${pulse} 1.5s ease-in-out infinite`
        : lowBalance
          ? `${pulseRed} 1s ease-in-out infinite`
          : 'none',
    }}
  >
    {children}
  </Box>
);

const ShakeAnimation = ({
  children,
  criticalBalance,
}: {
  children: React.ReactNode;
  criticalBalance: boolean;
}) => (
  <Box
    sx={{
      display: 'inline-block',
      animation: criticalBalance ? `${shake} 0.5s ease-in-out infinite` : 'none',
    }}
  >
    {children}
  </Box>
);

const Spinner = ({ size = 16 }: { size?: number }) => (
  <Box
    sx={{
      width: size,
      height: size,
      border: '2px solid',
      borderColor: 'currentColor',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: `${spin} 1s linear infinite`,
      display: 'inline-block',
    }}
  />
);

const SuccessCheckmark = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'success.main',
      color: 'common.white',
      animation: `${bounce} 0.5s ease-in-out`,
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  </Box>
);

const LoadingText = ({ text = 'Loading...' }: { text?: string }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    <Spinner size={14} />
    <Typography
      variant="inherit"
      sx={{
        background: `linear-gradient(90deg,
          currentColor 25%,
          rgba(128, 128, 128, 0.5) 50%,
          currentColor 75%
        )`,
        backgroundSize: '200% 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        animation: `${shimmer} 1.5s ease-in-out infinite`,
      }}
    >
      {text}
    </Typography>
  </Box>
);

export function BalanceDisplay({
  variant = 'default',
  isSuperAdmin = false,
  noActivePlan = false,
  sx,
  ...other
}: BalanceDisplayProps) {
  const { t } = useTranslate('translation');

  const theme = useTheme();
  const [getLowBalance, { data: balanceData, isLoading }] = useGetLowBalanceMutation();
  const [addBalance, { data: addBalanceData, isLoading: isAddBalanceLoading }] =
    useAddBalanceMutation();
  const [chargeEstimate, { data: chargeEstimateData, isLoading: isChargeEstimateLoading }] =
    useChargeEstimateMutation();
  const [balanceAnchorEl, setBalanceAnchorEl] = useState<HTMLElement | null>(null);
  const [addFundsAmount, setAddFundsAmount] = useState<string>('');
  const [isProcessingFunds, setIsProcessingFunds] = useState(false);
  const [showAddFundsForm, setShowAddFundsForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [animateAmount, setAnimateAmount] = useState(false);
  const [clickedButton, setClickedButton] = useState<number | null>(null);
  const [showMoneyEffect, setShowMoneyEffect] = useState(false);
  const [balanceUpdateAnimation, setBalanceUpdateAnimation] = useState(false);
  const [previousBalance, setPreviousBalance] = useState<number>(0);
  const [estimateItem, setEstimateItem] = useState<any>(null);

  const balancePopoverOpen = Boolean(balanceAnchorEl);

  useEffect(() => {
    getLowBalance({});
  }, [getLowBalance]);

  // Track balance changes and trigger animation
  const balanceInfo = (balanceData as BalanceApiResponse)?.data?.balance;
  const mainBalance = balanceInfo?.balance || 0;
  const accountantBalance = balanceInfo?.balance_accountant || 0;
  const availableBalance = balanceInfo?.balance_avail || 0;

  const isLowBalance = availableBalance < 10;
  const isCriticalBalance = availableBalance < 2;

  useEffect(() => {
    const currentBalance = balanceInfo?.balance || 0;
    if (previousBalance > 0 && currentBalance > previousBalance) {
      setBalanceUpdateAnimation(true);
      setTimeout(() => setBalanceUpdateAnimation(false), 2000);
    }
    setPreviousBalance(currentBalance);
  }, [balanceInfo?.balance, previousBalance]);

  const formatSARCurrency = (amount: number) => {
    const formatNumber = (num: number): string => {
      if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
      }
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      }
      return num.toFixed(2);
    };

    return `${formatNumber(amount)}`;
  };

  const handleBalanceClick = (event: React.MouseEvent<HTMLElement>) => {
    setBalanceAnchorEl(event.currentTarget);
  };

  const handleBalanceClose = () => {
    setBalanceAnchorEl(null);
    setAddFundsAmount('');
    setShowAddFundsForm(false);
  };

  const cardDialog = useBoolean(false);
  const cardPaymentDialog = useBoolean(false);
  const handleAddFunds = async (amount?: number) => {
    const amountToAdd = amount || parseFloat(addFundsAmount);
    if (!amountToAdd || amountToAdd <= 0) return;
    cardDialog.onTrue();

    const res = await chargeEstimate({ items: [{ amount: amountToAdd * 100 }] }).unwrap();
    setEstimateItem(res?.data?.estimate);
    // setIsProcessingFunds(true);
    // setShowSuccess(false);
    // setShowMoneyEffect(true);

    // try {
    //   const response = await addBalance({ amount: amountToAdd }).unwrap();

    //   toast.success(
    //     t('BALANCE_ADD_SUCCESS', {
    //       amount: amountToAdd.toFixed(2),
    //     })
    //   );
    //   setShowSuccess(true);

    //   await new Promise((resolve) => setTimeout(resolve, 1500));
    //   getLowBalance({});
    //   setAddFundsAmount('');
    //   setShowSuccess(false);
    //   setShowMoneyEffect(false);
    //   handleBalanceClose();
    // } catch (error: any) {
    //   console.error('Error adding funds:', error);

    //   const errorMessage =
    //     error?.data?.message === 'Tenant not properly linked to billing'
    //       ? t('TENANT_NOT_LINKED_BILLING')
    //       : error?.data?.message || error?.message || 'Failed to add funds. Please try again.';

    //   toast.error(errorMessage);
    //   setShowMoneyEffect(false);
    // } finally {
    //   setIsProcessingFunds(false);
    // }
  };

  const onSuccess = (data: { id: string; status: string }) => {
    const { id, status } = data;

    if (status === 'paid') {
      toast.success(t('PAYMENT_SUCCESS'));
    } else if (status === 'failed' || status === 'cancelled') {
      toast.error(t('PAYMENT_FAILED'));
    } else {
      console.warn('Unknown payment status:', status);
    }
  };

  const handlePaymentClose = () => {
    cardPaymentDialog.onFalse();
    getLowBalance({});
    setBalanceAnchorEl(null);
    setAddFundsAmount('');
    setShowAddFundsForm(false);
    setEstimateItem(null);
  };

  const handleQuickAmountClick = (amount: number) => {
    setClickedButton(amount);
    setAnimateAmount(true);

    setAddFundsAmount((prev) => {
      const current = parseFloat(prev) || 0;
      return (current + amount).toString();
    });

    // Reset animations
    setTimeout(() => {
      setClickedButton(null);
      setAnimateAmount(false);
    }, 600);
  };

  const onConfirmaHandler = async (item: any) => {
    cardDialog.onFalse();

    setTimeout(() => {
      setEstimateItem(item);
      cardPaymentDialog.onTrue();
    }, 50);
  };

  if (isLoading) {
    return <LoadingText />;
  }

  if (variant === 'compact') {
    return (
      <>
        <Box
          onClick={handleBalanceClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
            ...sx,
          }}
          {...other}
        >
          <ShakeAnimation criticalBalance={isCriticalBalance}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor:
                  availableBalance > 0
                    ? isLowBalance
                      ? theme.palette.warning.lighter
                      : theme.palette.success.lighter
                    : theme.palette.error.lighter,
                transition: 'all 0.3s ease',
                transform: balancePopoverOpen ? 'rotate(10deg)' : 'rotate(0deg)',
              }}
            >
              <Iconify
                icon="mdi:wallet"
                width={20}
                height={20}
                sx={{
                  color:
                    availableBalance > 0
                      ? isLowBalance
                        ? theme.palette.warning.main
                        : theme.palette.success.main
                      : theme.palette.error.main,
                }}
              />
            </Box>
          </ShakeAnimation>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1 }}
            >
              {t('AVAILABLE_BALANCE')}
            </Typography>
            <PulseAnimation loading={isLoading} lowBalance={isLowBalance}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color:
                    availableBalance > 0
                      ? isLowBalance
                        ? 'warning.main'
                        : 'success.main'
                      : 'error.main',
                  animation:
                    balanceUpdateAnimation && !balancePopoverOpen
                      ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                      : 'none',
                  transition: 'all 0.3s ease-in-out',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isLoading ? (
                  <LoadingText text="Loading..." />
                ) : (
                  <>
                    <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />{' '}
                    {formatSARCurrency(availableBalance)}
                  </>
                )}
              </Typography>
            </PulseAnimation>
          </Box>
          <Iconify
            icon="eva:chevron-down-fill"
            width={16}
            height={16}
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              transition: 'transform 0.2s ease-in-out',
              transform: balancePopoverOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        <Popover
          open={balancePopoverOpen}
          anchorEl={balanceAnchorEl}
          onClose={handleBalanceClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 1.5,
                ml: 0.75,
                width: 320,
                maxHeight: 275,
                overflowY: 'auto',
                boxShadow: theme.customShadows?.dropdown || theme.shadows[8],
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transformOrigin: 'top right',
                animation: balancePopoverOpen
                  ? 'scaleIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none',
                '@keyframes scaleIn': {
                  '0%': { opacity: 0, transform: 'scale(0.95)' },
                  '100%': { opacity: 1, transform: 'scale(1)' },
                },
              },
            },
          }}
        >
          <Card sx={{ boxShadow: 'none', bgcolor: 'transparent' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
                {t('BALANCE_OVERVIEW')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor:
                          availableBalance > 0
                            ? isLowBalance
                              ? theme.palette.warning.main
                              : theme.palette.success.main
                            : theme.palette.error.main,
                        animation: `${pulse} 2s infinite`,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('AVAILABLE_BALANCE')}
                    </Typography>
                  </Box>
                  <PulseAnimation loading={isLoading} lowBalance={false}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color:
                          availableBalance > 0
                            ? isLowBalance
                              ? 'warning.main'
                              : 'success.main'
                            : 'error.main',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isLoading ? (
                        <LoadingText text="..." />
                      ) : (
                        <>
                          <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />{' '}
                          {formatSARCurrency(availableBalance)}
                        </>
                      )}
                    </Typography>
                  </PulseAnimation>
                </Box>

                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.info.main,
                        animation: `${pulse} 2s infinite 0.2s`,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('MAIN_BALANCE')}
                    </Typography>
                  </Box>
                  <PulseAnimation loading={isLoading} lowBalance={false}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: mainBalance > 0 ? 'info.main' : 'text.secondary',
                        animation: balanceUpdateAnimation
                          ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                          : 'none',
                        transition: 'all 0.3s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isLoading ? (
                        <LoadingText text="..." />
                      ) : (
                        <>
                          <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />{' '}
                          {formatSARCurrency(mainBalance)}
                        </>
                      )}
                    </Typography>
                  </PulseAnimation>
                </Box>

                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        animation: `${pulse} 2s infinite 0.4s`,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('ACCOUNTANT_BALANCE')}
                    </Typography>
                  </Box>
                  <PulseAnimation loading={isLoading} lowBalance={false}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: accountantBalance > 0 ? 'primary.main' : 'text.secondary',
                        animation: balanceUpdateAnimation
                          ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                          : 'none',
                        transition: 'all 0.3s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {isLoading ? (
                        <LoadingText text="..." />
                      ) : (
                        <>
                          <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />{' '}
                          {formatSARCurrency(accountantBalance)}
                        </>
                      )}
                    </Typography>
                  </PulseAnimation>
                </Box>
              </Box>

              {/* Add Funds Toggle UI */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      '& > *': {
                        color: 'primary.main',
                      },
                    },
                  }}
                  onClick={() => setShowAddFundsForm((prev) => !prev)}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      transition: 'color 0.2s ease-in-out',
                    }}
                  >
                    {t('ADD_FUNDS')}
                  </Typography>
                  <Iconify
                    icon={showAddFundsForm ? 'eva:chevron-up-outline' : 'eva:chevron-down-outline'}
                    width={18}
                    height={18}
                    sx={{
                      color: 'text.secondary',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: showAddFundsForm ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    opacity: showAddFundsForm ? 1 : 0,
                    maxHeight: showAddFundsForm ? '250px' : '0px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: showAddFundsForm ? 'translateY(0)' : 'translateY(-10px)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 2,
                      animation: showAddFundsForm ? `${fadeInUp} 0.3s ease-out 0.1s both` : 'none',
                    }}
                  >
                    <Box sx={{ position: 'relative', flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder={t('ENTER_AMOUNT')}
                        value={addFundsAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow positive numbers, no negative, no decimals, no commas
                          if (value === '' || (/^\d+$/.test(value) && parseFloat(value) >= 0)) {
                            setAddFundsAmount(value);
                          }
                        }}
                        error={
                          addFundsAmount?.includes('.') ||
                          addFundsAmount?.includes(',') ||
                          noActivePlan ||
                          (addFundsAmount !== '' && parseFloat(addFundsAmount) < 0)
                        }
                        helperText={
                          addFundsAmount?.includes('.') || addFundsAmount?.includes(',')
                            ? t('DECIMAL_NOT_ALLOWED')
                            : addFundsAmount !== '' && parseFloat(addFundsAmount) < 0
                              ? t('NEGATIVE_NOT_ALLOWED')
                              : ''
                        }
                        disabled={noActivePlan}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            animation: animateAmount
                              ? `${numberIncrement} 0.6s ease-in-out`
                              : 'none',
                          },
                          '& .MuiInputBase-input': {
                            transition: 'all 0.2s ease-in-out',
                            '&::placeholder': {
                              transition: 'all 0.2s ease-in-out',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAddFunds()}
                      disabled={
                        !addFundsAmount ||
                        parseFloat(addFundsAmount) <= 0 ||
                        isProcessingFunds ||
                        noActivePlan ||
                        addFundsAmount?.includes('.') ||
                        addFundsAmount?.includes(',')
                      }
                      sx={{
                        minWidth: 80,
                        borderRadius: 1,
                        fontWeight: 600,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        animation: showAddFundsForm
                          ? `${scaleIn} 0.3s ease-out 0.15s both`
                          : 'none',
                        '&:hover:not(:disabled)': {
                          transform: 'translateY(-2px) scale(1.02)',
                          boxShadow: theme.shadows[6],
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '100%',
                            height: '100%',
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%) scale(0)',
                            animation: `${ripple} 0.6s ease-out`,
                          },
                        },
                        '&:active:not(:disabled)': {
                          transform: 'translateY(0) scale(0.98)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        {showSuccess ? (
                          <SuccessCheckmark />
                        ) : isProcessingFunds ? (
                          <>
                            <Spinner size={16} />
                            {showMoneyEffect && (
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                {t('ADDING')}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            <Iconify
                              icon="mdi:plus-circle-outline"
                              width={16}
                              height={16}
                              sx={{
                                transition: 'transform 0.2s ease-in-out',
                              }}
                            />
                            {t('ADD')}
                          </>
                        )}
                      </Box>
                    </Button>
                  </Box>
                  {noActivePlan && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="error">
                        {t('NO_ACTIVE_PLAN')}
                      </Typography>
                      <Typography variant="caption" color="error">
                        {t('NO_ACTIVE_PLAN_DESC')}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      animation: showAddFundsForm ? `${fadeInUp} 0.4s ease-out 0.2s both` : 'none',
                    }}
                  >
                    {[50, 100, 200, 500].map((amount, index) => (
                      <Box
                        key={amount}
                        onClick={() => !noActivePlan && handleQuickAmountClick(amount)}
                        sx={{
                          flex: 1,
                          p: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          textAlign: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          animation: showAddFundsForm
                            ? `${scaleIn} 0.3s ease-out ${0.1 + index * 0.1}s both`
                            : 'none',
                          backgroundColor:
                            clickedButton === amount
                              ? theme.palette.primary.main
                              : theme.palette.mode === 'dark'
                                ? theme.palette.grey[800]
                                : theme.palette.grey[200],
                          color:
                            clickedButton === amount
                              ? theme.palette.primary.contrastText
                              : theme.palette.mode === 'dark'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                          border: `1px solid ${
                            clickedButton === amount
                              ? theme.palette.primary.main
                              : theme.palette.mode === 'dark'
                                ? theme.palette.grey[700]
                                : theme.palette.grey[300]
                          }`,
                          transform: clickedButton === amount ? 'scale(0.95)' : 'scale(1)',
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.dark
                                : theme.palette.primary.lighter,
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.contrastText
                                : theme.palette.primary.main,
                            transform: 'translateY(-3px) scale(1.02)',
                            boxShadow: theme.shadows[4],
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(255, 255, 255, 0.1)',
                              animation: `${ripple} 0.6s ease-out`,
                            },
                          },
                          '&:active': {
                            transform: 'translateY(-1px) scale(0.98)',
                          },
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {amount}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Popover>

        <AddBalanceDialog
          isOpen={cardDialog.value}
          onCloseHandler={cardDialog.onFalse}
          onConfirmHandler={() => onConfirmaHandler(estimateItem)}
          t={t}
          isLoading={isChargeEstimateLoading}
          estimateItem={estimateItem}
          isSuperAdmin={isSuperAdmin}
        />

        {estimateItem && (
          <CardDetailsDialog
            open={cardPaymentDialog.value}
            onClose={handlePaymentClose}
            onSuccess={onSuccess}
            estimateItem={estimateItem || null}
            currency={estimateItem?.currency_code || 'SAR'}
            cardDetailsKey="addBalance"
            isSuperAdmin={isSuperAdmin}
            actionPayload={{
              topUp: estimateItem?.total || 0,
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          ...sx,
        }}
        {...other}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('AVAILABLE_BALANCE_LABEL')}
          </Typography>
          <PulseAnimation loading={isLoading} lowBalance={isLowBalance}>
            <ShakeAnimation criticalBalance={isCriticalBalance}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color:
                    availableBalance > 0
                      ? isLowBalance
                        ? 'warning.main'
                        : 'success.main'
                      : 'error.main',
                  animation: balanceUpdateAnimation
                    ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                    : 'none',
                  transition: 'all 0.3s ease-in-out',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />
                {formatSARCurrency(availableBalance)}
              </Typography>
            </ShakeAnimation>
          </PulseAnimation>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('MAIN_BALANCE_LABEL')}
          </Typography>
          <PulseAnimation loading={isLoading} lowBalance={false}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: mainBalance > 0 ? 'info.main' : 'text.secondary',
                animation: balanceUpdateAnimation
                  ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                  : 'none',
                transition: 'all 0.3s ease-in-out',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} />
              {formatSARCurrency(mainBalance)}
            </Typography>
          </PulseAnimation>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('ACCOUNTANT_LABEL')}
          </Typography>
          <PulseAnimation loading={isLoading} lowBalance={false}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: accountantBalance > 0 ? 'primary.main' : 'text.secondary',
                animation: balanceUpdateAnimation
                  ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                  : 'none',
                transition: 'all 0.3s ease-in-out',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Iconify icon="lucide-saudi-riyal" sx={{ mr: 0.5 }} width={18} />
              {formatSARCurrency(accountantBalance)}
            </Typography>
          </PulseAnimation>
        </Box>
      </Box>
    </>
  );
}
